import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { recoilLoginState } from '../../RecoilStore';
import { useDropzone } from 'react-dropzone';
import imageCompression from "browser-image-compression";
import { format } from "date-fns";
import Loading from '../../components/Loading';
import { Icon, PageHead } from '../AdminLayout';

interface PracticeRow {
  id: number;
  title: string;
  date: string;
  content: string; // JSON or string by contentSort
  contentSort: 'text' | 'onlyimage';
}

export default function EditPractice() {
  const navigate = useNavigate();
  const isLogin = useRecoilValue(recoilLoginState);
  const [rows, setRows] = useState<PracticeRow[]>([]);
  const [selected, setSelected] = useState<PracticeRow | null>(null);
  const [contentSort, setContentSort] = useState<'text' | 'onlyimage'>('text');
  const [textContent, setTextContent] = useState<any[]>([]);
  const [imageFilename, setImageFilename] = useState<string>('');
  
  // 이미지 첨부 관련 상태
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  
  // 이미지 첨부 함수
  const currentDate = new Date();
  const date = format(currentDate, 'yyyy-MM-dd');
  
  // 임의의 영문+숫자 문자열 생성 함수
  const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // 이미지 압축 및 파일명 생성 함수 (jpg/jpeg/png만 허용)
  const processImageFiles = async (acceptedFiles: File[]) => {
    try {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const validFiles = acceptedFiles.filter(f => allowedTypes.includes(f.type));
      if (validFiles.length === 0) {
        alert('jpg, jpeg, png 형식의 이미지 파일만 업로드할 수 있습니다.');
        return [];
      }

      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1000
      };
      const resizedFiles = await Promise.all(
        validFiles.map(async (file) => {
          setImageLoading(true);
          const resizingBlob = await imageCompression(file, options);
          return resizingBlob;
        })
      );
      
      const adminId = 'admin'; // 관리자 ID
      const fileCopies = resizedFiles.map((resizedFile, index) => {
        const randomString = generateRandomString(10);
        const originalFile = validFiles[index];
        const mime = originalFile.type || '';
        const extension = mime.includes('png')
          ? '.png'
          : mime.includes('jpeg') || mime.includes('jpg')
            ? '.jpg'
            : '.jpg';
        return new File([resizedFile], `${date}${adminId}_${randomString}${extension}`, {
          type: originalFile.type,
        });
      });
      setImageLoading(false);
      return fileCopies;
    } catch (error) {
      console.error('이미지 리사이징 중 오류 발생:', error);
      setImageLoading(false);
      return [];
    }
  };
  
  // 드롭존 설정
  const imageDropzone = useDropzone({ 
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      const processedFiles = await processImageFiles(acceptedFiles);
      setImageFiles(processedFiles);
    }, []) 
  });
  
  // 첨부 이미지 삭제 함수
  const deleteImage = (idx: number) => {
    const copy = [...imageFiles];
    const newItems = copy.filter((item, i) => i !== idx);
    setImageFiles(newItems);
  };

  const load = async () => {
    const res = await axios.get(`${MainURL}/main/getpractice`);
    if (res.data) {
      setRows(res.data);
    }
  };

  useEffect(() => {
    if (!isLogin) {
      alert('로그인이 필요합니다.');
      navigate('/admin');
      return;
    }
    load();
  }, []);

  const selectRow = (row: PracticeRow) => {
    setSelected(row);
    setContentSort(row.contentSort);
    if (row.contentSort === 'text') {
      // 섹션 구조 [{ title, content: [] }] 유지. 만약 라인 배열이면 무제목 섹션으로 변환
      try {
        const parsed = JSON.parse(row.content || '[]');
        if (Array.isArray(parsed)) {
          if (parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0] !== null && 'content' in parsed[0]) {
            setTextContent(parsed as any[]);
          } else {
            const lines = (parsed as any[]).filter((x) => typeof x === 'string');
            setTextContent([{ title: '', content: lines }] as any[]);
          }
        } else {
          setTextContent([]);
        }
      } catch {
        setTextContent([]);
      }
      setImageFilename('');
    } else {
      setTextContent([]);
      setImageFilename(row.content || '');
    }
  };

  const onUploadMainImage = async (file: File): Promise<string | null> => {
    try {
      console.log('이미지 업로드 시작:', file.name, file.size, file.type);
      const form = new FormData();
      form.append('img', file);
      
      console.log('FormData 생성됨, 업로드 URL:', `${MainURL}/main/upload/mainimage`);
      
      const res = await axios.post(`${MainURL}/main/upload/mainimage`, form, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      console.log('서버 응답:', res.data);
      
      if (res.data && res.data.filename) {
        console.log('업로드 성공, 파일명:', res.data.filename);
        return res.data.filename;
      } else {
        console.error('서버 응답에 filename이 없음:', res.data);
        return null;
      }
    } catch (error: any) {
      console.error('이미지 업로드 실패:', error);
      if (error.response) {
        console.error('서버 응답 오류:', error.response.data);
        console.error('HTTP 상태:', error.response.status);
      }
      return null;
    }
  };

  const save = async () => {
    if (!selected) return;
    const payload = {
      id: selected.id,
      title: selected.title,
      contentSort,
      content: contentSort === 'text' ? JSON.stringify(textContent) : imageFilename
    };
    const res = await axios.post(`${MainURL}/main/practice/update`, payload);
    if (res.data === true) {
      alert('저장되었습니다.');
      navigate('/admin/main');
    } else {
      alert('저장에 실패했습니다.');
    }
  };

  // 섹션 제목 수정
  const updateTextSectionTitle = (index: number, value: string) => {
    const copy = (textContent || []).map((sec: any, i: number) => i === index ? { ...sec, title: value } : sec);
    setTextContent(copy);
  };

  // 섹션 내 항목 수정
  const updateTextSectionItem = (sectionIndex: number, itemIndex: number, value: string) => {
    const copy = (textContent || []).map((sec: any, i: number) => {
      if (i !== sectionIndex) return sec;
      const content = (sec.content || []).map((c: string, ci: number) => ci === itemIndex ? value : c);
      return { ...sec, content };
    });
    setTextContent(copy);
  };

  const applyImage = async () => {
    if (imageFiles.length === 0) return;
    const filename = await onUploadMainImage(imageFiles[0]);
    if (filename) {
      setImageFilename(filename);
      setImageFiles([]);
      alert('이미지가 적용되었습니다.');
    } else {
      alert('이미지 업로드에 실패했습니다.');
    }
  };

  return (
    <>
      <PageHead title="후원 · 실습 관리" desc="후원 · 자원봉사 · 실습 안내 페이지의 내용을 수정합니다. 수정할 페이지를 먼저 선택하세요." />

      <div className="ad-tabs" role="tablist">
        {rows.map((r) => (
          <button type="button" role="tab" key={r.id} className={`ad-tab ${selected?.id === r.id ? 'is-on' : ''}`} onClick={()=>selectRow(r)}>
            {r.title}
          </button>
        ))}
      </div>

      {selected ? (
        <div className="ad-card">
          <div className="ad-card__head">
            <h2>{selected.title}</h2>
            <p>ID {selected.id}</p>
          </div>

          <div className="ad-card__pad">
            <div className="ad-field" style={{ maxWidth: 280 }}>
              <label htmlFor="practiceSort">컨텐츠 유형</label>
              <select id="practiceSort" className="ad-input" value={contentSort} onChange={(e)=>setContentSort(e.target.value as any)}>
                <option value='text'>텍스트 컨텐츠</option>
                <option value='onlyimage'>이미지 컨텐츠</option>
              </select>
            </div>

            {contentSort === 'text' ? (
              <div className="ad-field">
                <label>섹션 <em>{(textContent || []).length}개</em></label>
                <div className="ad-rep">
                  {(textContent || []).map((sec: any, si: number) => (
                    <div key={si} className="ad-rep__item">
                      <div className="ad-rep__top">
                        <span className="ad-rep__no">{si + 1}</span>
                        <span>섹션 {si + 1}</span>
                        <button type="button" className="ad-rep__del"
                          onClick={() => {
                            if (window.confirm('이 섹션을 삭제하시겠습니까?')) {
                              const copy = (textContent || []).filter((_: any, i: number) => i !== si);
                              setTextContent(copy);
                            }
                          }}>
                          섹션 삭제
                        </button>
                      </div>

                      <div className="ad-field" style={{ marginBottom: 12 }}>
                        <input
                          className='ad-input'
                          value={sec.title || ''}
                          onChange={(e)=> updateTextSectionTitle(si, e.target.value)}
                          placeholder="섹션 제목을 입력하세요"
                        />
                      </div>

                      {(sec.content || []).map((item: string, ii: number) => (
                        <div key={ii} className="ad-rep__line">
                          <input
                            className='ad-input'
                            value={item}
                            onChange={(e)=> updateTextSectionItem(si, ii, e.target.value)}
                            placeholder={`항목 ${ii + 1}을 입력하세요`}
                          />
                          <button type="button" className="ad-btn ad-btn--ghost ad-btn--icon is-danger" aria-label="항목 삭제" style={{ height: 42 }}
                            onClick={() => {
                              if (window.confirm('이 항목을 삭제하시겠습니까?')) {
                                const copy = (textContent || []).map((s: any, i: number) => {
                                  if (i === si) {
                                    const newContent = (s.content || []).filter((_: string, ci: number) => ci !== ii);
                                    return { ...s, content: newContent };
                                  }
                                  return s;
                                });
                                setTextContent(copy);
                              }
                            }}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      ))}
                      <button type="button" className="ad-btn ad-btn--sm" style={{ marginTop: 4 }}
                        onClick={() => {
                          const copy = (textContent || []).map((s: any, i: number) => i === si ? { ...s, content: [ ...(s.content || []), '' ] } : s);
                          setTextContent(copy);
                        }}>
                        <Icon name="plus" />항목 추가
                      </button>
                    </div>
                  ))}
                </div>
                <div className="ad-rep__foot">
                  <button type="button" className="ad-btn ad-btn--primary ad-btn--sm"
                    onClick={() => setTextContent([ ...(textContent || []), { title: '', content: [''] } ])}>
                    <Icon name="plus" />새 섹션 추가
                  </button>
                </div>
              </div>
            ) : (
              <div className="ad-field">
                <label>이미지</label>
                <div className="ad-upload">
                  {imageFilename
                    ? <img className="ad-thumb ad-thumb--md" src={`${MainURL}/images/mainimages/${imageFilename}`} alt="" />
                    : <span className="ad-thumb ad-thumb--md ad-thumb--empty"><Icon name="image" /></span>}
                  <div style={{ display:'grid', gap: 8, justifyItems: 'start' }}>
                    {imageLoading ? (
                      <div className="ad-loading"><Loading/></div>
                    ) : (
                      <div {...imageDropzone.getRootProps()}>
                        <input {...imageDropzone.getInputProps()} />
                        <span className="ad-drop"><Icon name="image" />{imageFiles.length > 0 ? '다시 첨부하기' : '이미지 첨부하기'}</span>
                      </div>
                    )}
                    {imageFilename && (
                      <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger"
                        onClick={() => {
                          if (window.confirm('이 이미지를 삭제하시겠습니까?')) {
                            setImageFilename('');
                            alert('이미지가 삭제되었습니다.');
                          }
                        }}>
                        이미지 삭제
                      </button>
                    )}
                  </div>
                </div>

                {imageFiles.length > 0 && (
                  <>
                    <div className="ad-files">
                      {imageFiles.map((item: File, index: number) => (
                        <div key={index} className="ad-file">
                          <img src={URL.createObjectURL(item)} alt="" />
                          <span>{item.name}</span>
                          <button type="button" className="ad-btn ad-btn--ghost ad-btn--icon is-danger" aria-label="첨부 취소" onClick={() => deleteImage(index)}>
                            <Icon name="close" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div>
                      <button type="button" className="ad-btn ad-btn--primary ad-btn--sm" style={{ marginTop: 4 }} onClick={applyImage}>
                        이미지 적용하기
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="ad-card__foot">
            <span className="grow">변경 사항은 저장 후 홈페이지에 반영됩니다.</span>
            <button type="button" className="ad-btn" onClick={()=>navigate('/admin/main')}>취소</button>
            <button type="button" className="ad-btn ad-btn--primary" onClick={save}>저장</button>
          </div>
        </div>
      ) : (
        <div className="ad-card ad-card__pad">
          <div className="ad-empty">
            <Icon name="support" />
            <p>위에서 수정할 페이지를 선택하세요.</p>
          </div>
        </div>
      )}
    </>
  );
}
